import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { SellerRequestStatus, SellerRequestView } from '@sellit/shared';
import { platform } from '../lib/api';
import { setAppTitle, timeAgo } from '../lib/format';
import { errorText } from './common';

const TABS: { status: SellerRequestStatus; label: string }[] = [
  { status: 'NEW', label: 'New' },
  { status: 'INVITED', label: 'Invited' },
  { status: 'DECLINED', label: 'Declined' },
  { status: 'SPAM', label: 'Spam' },
];

/** Start Selling requests. Creating a seller from one pre-fills the New seller form. */
export function RequestsPage() {
  const [tab, setTab] = useState<SellerRequestStatus>('NEW');
  const [requests, setRequests] = useState<SellerRequestView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setAppTitle('Requests'), []);
  useEffect(() => {
    setRequests(null);
    platform.sellerRequests(tab).then((r) => setRequests(r.requests), (err) => setError(errorText(err)));
  }, [tab]);

  async function move(r: SellerRequestView, status: SellerRequestStatus) {
    try {
      await platform.setSellerRequestStatus(r.id, status);
      setRequests((list) => list?.filter((x) => x.id !== r.id) ?? null);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <main className="page wide pa-page">
      <header className="pa-head">
        <h1>Start Selling requests</h1>
      </header>
      <p className="muted small">People who filled in the Start Selling form. No account exists until you create one.</p>
      <div className="tabs underline" role="tablist">
        {TABS.map((t) => (
          <button key={t.status} role="tab" aria-selected={tab === t.status} className={tab === t.status ? 'on' : ''} onClick={() => setTab(t.status)}>
            {t.label}
          </button>
        ))}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!requests ? (
        !error && <p className="muted">Loading…</p>
      ) : requests.length === 0 ? (
        <p className="empty muted">{tab === 'NEW' ? 'No new requests.' : 'Nothing here.'}</p>
      ) : (
        <ul className="pa-list">
          {requests.map((r) => (
            <li key={r.id} className="card request">
              <div className="pa-row-main">
                <strong>{r.name}</strong>
                <span className="muted small">{r.email}{r.saleName ? ` · wants “${r.saleName}”` : ''}</span>
                <span className="muted small">
                  {timeAgo(r.createdAt)}
                  {r.handledByName && r.handledAt ? ` · ${TABS.find((t) => t.status === r.status)!.label.toLowerCase()} by ${r.handledByName} ${timeAgo(r.handledAt)}` : ''}
                </span>
              </div>
              <div className="actions">
                {r.status === 'NEW' && (r.existingSellerId ? (
                  <Link className="btn primary" to={`/platform-admin/sellers/${r.existingSellerId}`}>Already a seller: view</Link>
                ) : (
                  <Link className="btn primary" to={`/platform-admin/sellers/new?request=${r.id}`}>Create seller</Link>
                ))}
                {r.status === 'INVITED' && r.sellerId && <Link className="btn" to={`/platform-admin/sellers/${r.sellerId}`}>View seller</Link>}
                {r.status !== 'NEW' && r.status !== 'INVITED' && <button className="btn" onClick={() => move(r, 'NEW')}>Move to New</button>}
                {r.status === 'NEW' && <button className="btn" onClick={() => move(r, 'DECLINED')}>Decline</button>}
                {r.status === 'NEW' && <button className="link small muted" onClick={() => move(r, 'SPAM')}>Spam</button>}
                <a className="link small" href={`mailto:${r.email}`}>Email</a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
